import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateAccount } from '@app/sdk';
import { useAddTenantAccountId } from '../../../GlobalContextProvider';
import { AccountIdContext } from '../AccountIdContext';

type QuickProvisionOption = {
  label: string;
  productId: string;
  creditLimit: string;
  creditLimitIncreasePreference: string;
  interestRates: {
    spendType: string;
    feeCategory: null;
    subledgerType: null;
    rate: number;
  }[];
  paymentDay: null;
  statementDay: number;
};

const quickProvisionOptions: Record<string, QuickProvisionOption[]> = {
  dev: [
    {
      label: 'Team 2 - BOE, CL: 1000',
      productId: '1f59d22f-7024-44ed-b9c4-20dbb4384254',
      creditLimit: '1000',
      creditLimitIncreasePreference: 'Automatic',
      interestRates: [
        { spendType: 'Purchase', feeCategory: null, subledgerType: null, rate: 0.123 },
        { spendType: 'Instalment', feeCategory: null, subledgerType: null, rate: 0.456 },
      ],
      paymentDay: null,
      statementDay: 1,
    },
    {
      label: 'Smoke Test Account, CL: 1000',
      productId: 'b52d5de2-d018-4bfb-8106-3cf9751ee459',
      creditLimit: '1000',
      creditLimitIncreasePreference: 'Automatic',
      interestRates: [
        { spendType: 'Purchase', feeCategory: null, subledgerType: null, rate: 0.123 },
        { spendType: 'Instalment', feeCategory: null, subledgerType: null, rate: 0.126 },
      ],
      paymentDay: null,
      statementDay: 1,
    },
  ],
  stg: [
    {
      label: 'STG Smoke Test Account, CL: 1000',
      productId: 'f7708a02-9bc8-4ac5-a5cc-947c77d19050',
      creditLimit: '1000',
      creditLimitIncreasePreference: 'Automatic',
      interestRates: [
        { spendType: 'Purchase', feeCategory: null, subledgerType: null, rate: 0.123 },
        { spendType: 'Instalment', feeCategory: null, subledgerType: null, rate: 0.126 },
      ],
      paymentDay: null,
      statementDay: 1,
    },
    {
      label: 'Team 2 - BOE, CL: 1000',
      productId: '30ae9078-b9bc-4277-8972-04049d100527',
      creditLimit: '1000',
      creditLimitIncreasePreference: 'Automatic',
      interestRates: [
        { spendType: 'Purchase', feeCategory: null, subledgerType: null, rate: 0.123 },
        { spendType: 'Instalment', feeCategory: null, subledgerType: null, rate: 0.456 },
      ],
      paymentDay: null,
      statementDay: 1,
    },
  ],
};

const environment = import.meta.env.VITE_SCOPE_ENVIRONMENT?.toLowerCase();

export default function QuickProvisionAccount() {
  const navigate = useNavigate();
  const { setAccountId } = useContext(AccountIdContext);
  const addAccountId = useAddTenantAccountId();
  const options = useMemo(() => quickProvisionOptions[environment] ?? [], []);
  const [selectedOption, setSelectedOption] = useState(0);
  const { mutateAsync: createAccount, isPending: isCreating } = useCreateAccount();

  useEffect(() => {
    setAccountId(undefined);
  }, [setAccountId]);

  const onCreateAccount = useCallback(async () => {
    const account = options[selectedOption];
    if (!account) {
      return;
    }

    const { accountId } = await createAccount({
      creditLimit: account.creditLimit,
      creditLimitIncreasePreference: account.creditLimitIncreasePreference,
      productId: account.productId,
      ratesList: account.interestRates,
      paymentDay: account.paymentDay,
      statementDay: account.statementDay,
    });

    localStorage.setItem(`${accountId}-billing-date`, new Date().toISOString());
    setAccountId(accountId);
    addAccountId(accountId);
    setTimeout(() => {
      navigate(`/accounts/${accountId}/summary`);
    }, 200);
  }, [addAccountId, createAccount, navigate, options, selectedOption, setAccountId]);

  if (!options.length) {
    return (
      <section>
        <h1>Quick Provision Account</h1>
        <div className="ui warning message">Quick provisioning is only available in DEV and STG.</div>
      </section>
    );
  }

  return (
    <section>
      <h1>Quick Provision Account</h1>
      <div className="ui form">
        <div className="field">
          <label htmlFor="quick-provision-option">Account template</label>
          <select
            id="quick-provision-option"
            className="ui fluid dropdown"
            value={selectedOption}
            onChange={(event) => setSelectedOption(Number(event.target.value))}
            disabled={isCreating}>
            {options.map((option, index) => (
              <option key={option.productId} value={index}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <button className="ui primary button" type="button" onClick={onCreateAccount} disabled={isCreating}>
          Create Account
        </button>
        {isCreating && <span className="ui active centered inline loader" />}
      </div>
    </section>
  );
}
